/**
 * The contract between the browser and `/api/subscribe`.
 *
 * Shared by both sides on purpose: if the request or response shape changes,
 * the compiler breaks the other end instead of letting the two drift apart at
 * runtime. Everything here is safe in a browser bundle - no IDs, no keys.
 */

/** Which side of the marketplace the visitor signed up as. */
export const ROLES = ['creator', 'buyer'] as const;
export type Role = (typeof ROLES)[number];

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/**
 * Intentionally permissive. Real validation is the double opt-in email - an address
 * that cannot receive the confirmation email never becomes a subscriber. This
 * only catches obvious typos before we spend a network round trip on them, and
 * runs on both sides so a hand-rolled POST gets the same treatment.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  return value.length <= 254 && EMAIL_PATTERN.test(value);
}

/** Field name of the honeypot input. Bots fill it in; humans never see it. */
export const HONEYPOT_FIELD = 'company';

/**
 * `resend` asks for a fresh confirmation email. It is a separate intent because
 * it is destructive on the provider side (see the provider's resend) and must
 * never be what a plain form submission triggers.
 */
export type Intent = 'subscribe' | 'resend';

export interface SubscribeRequest {
  email: string;
  role: Role;
  intent?: Intent;
  /** Honeypot. Always empty for a real submission. */
  [HONEYPOT_FIELD]?: string;
}

/**
 * Response body from `/api/subscribe`. A discriminated union so the client
 * cannot forget a case.
 *
 * - `pending` - accepted; the opt-in email is on its way.
 * - `error`   - `message` is already user-facing; the cause stays in our logs.
 */
export type SubscribeResponse = { status: 'pending' } | { status: 'error'; message: string };
